/**
 * FIX-LOG 82: after a redeploy, a page that was loaded from the previous build
 * still names that build's chunk files (e.g. `_auth-<old hash>.js`). They no
 * longer exist, so the next lazy route fails with "Failed to fetch dynamically
 * imported module". Vite reports every such failure as `vite:preloadError`;
 * we answer it with one page reload, which fetches the new index.html
 * (served no-cache) and therefore the new chunks.
 *
 * Loop guard: at most one reload per build. The build is identified by the
 * entry module's own URL, which is content-hashed, so a later deploy gets its
 * own single reload while a reload that comes back on the same (still broken)
 * build does not reload again — the error is shown instead.
 */

export const CHUNK_RELOAD_KEY = "ledger-core:chunk-reload-build";

export interface GuardStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/** True when this build may reload once now; records the claim. */
export function claimChunkReload(storage: GuardStorage | null, build: string): boolean {
  if (!storage) return false; // no way to remember the attempt → never risk a loop
  try {
    if (storage.getItem(CHUNK_RELOAD_KEY) === build) return false;
    storage.setItem(CHUNK_RELOAD_KEY, build);
    return true;
  } catch {
    return false;
  }
}

export interface ChunkReloadWindow {
  addEventListener(type: string, listener: (event: Event) => void): void;
  readonly sessionStorage: GuardStorage;
  readonly location: { reload(): void };
}

export function installChunkReload(win: ChunkReloadWindow, build: string): void {
  win.addEventListener("vite:preloadError", (event) => {
    let storage: GuardStorage | null = null;
    try {
      storage = win.sessionStorage;
    } catch {
      storage = null;
    }
    if (!claimChunkReload(storage, build)) return; // let the error reach the error screen
    event.preventDefault();
    win.location.reload();
  });
}
